import {
  useInfiniteScroll,
  UseInfiniteScrollParams,
  UseInfiniteScrollReturnType,
} from "shared/hooks/useInfiniteScroll.hook";
import {
  UseGetSavedCardsInfiniteQueryReturnType,
  useSeaSavedCardsInfiniteQuery,
  UseSeaSavedCardsInfiniteQueryParams,
} from "./useSavedCardsInfiniteQuery";

export type UseInfiniteScrollFetchParams<TElement extends HTMLElement> = Omit<
  UseInfiniteScrollParams<TElement>,
  "onReachEndOfList"
> &
  UseSeaSavedCardsInfiniteQueryParams;

type UseInfiniteScrollFetchReturnType = Pick<UseInfiniteScrollReturnType, "hasScroll"> &
  UseGetSavedCardsInfiniteQueryReturnType & {
    requestSeaMoreCards: () => void;
    shouldRenderSeaMoreButton: boolean;
    shouldRenderSeaInfiniteQueryLoader: boolean;
  };

export const useSeaInfiniteScrollFetch = <TElement extends HTMLElement>({
  containerRef,
  onSuccess,
  onSettled,
  shouldRequestOnMount,
  queryKey,
  initialFilter,
  ...restProps
}: UseInfiniteScrollFetchParams<TElement>): UseInfiniteScrollFetchReturnType => {
  const queryMeta = useSeaSavedCardsInfiniteQuery({
    onSuccess,
    onSettled,
    shouldRequestOnMount,
    queryKey,
    initialFilter,
    ...restProps,
  });
  const {
    requestMoreCards,
    canRequestMoreSeaCards: canRequestMore,
    isCardsLoading,
    isCardsFetching,
    isCardsLoadingNextPage,
  } = queryMeta;

  const shouldRenderInfiniteQueryLoader = isCardsLoading || isCardsFetching || isCardsLoadingNextPage;

  const { hasScroll } = useInfiniteScroll<TElement>({
    containerRef,
    onReachEndOfList: () => {
      if (!canRequestMore || shouldRenderInfiniteQueryLoader) return;
      requestMoreCards();
    },
  });

  return {
    ...queryMeta,
    requestSeaMoreCards: requestMoreCards,
    shouldRenderSeaMoreButton: !hasScroll && canRequestMore && !shouldRenderInfiniteQueryLoader,
    shouldRenderSeaInfiniteQueryLoader: shouldRenderInfiniteQueryLoader,
    hasScroll,
  };
};
